// Chip rows and hero claims for the minimal /photon landing page, derived
// from the release manifest (via generated/release-data.ts) so the page
// cannot drift from what the current release actually supports. Only
// entries backed by a production support row are surfaced - preview and
// planned releases stay off the landing page.

import {
  HARDWARE_CHIPS,
  MODEL_FAMILIES,
  RELEASE_MODELS,
  SUPPORT_MATRIX,
  slugify,
} from "./photon-data";
import type { ModelFamily, ReleaseModel, SupportRow } from "./release-types";

export type CatalogChip = {
  key: string;
  /** Display name, e.g. "Qwen 3.5" or "H100". */
  name: string;
  /** Mono detail suffix, e.g. "0.8B, 2B, 4B, 9B" or "SXM 80GB". */
  suffix: string;
  href: string;
  /** Family id for the brand mark; undefined for hardware. */
  mark?: string;
  /** Corner badge for the newest release cohort, e.g. "New 2.1". */
  newBadge?: string;
};

export type ChipGroup = {
  /** Mono group label, e.g. "Image + text → text" or "NVIDIA". */
  label: string;
  chips: CatalogChip[];
};

// ---- Models, grouped by modality ----
// One chip per family release ("Qwen 3.5", "Gemma 4") with its
// size variants as the suffix; families without size variants (Moondream)
// collapse into a single chip with releases as the suffix, newest first.
export const buildModelGroups = (
  families: ModelFamily[],
  releaseModels: ReleaseModel[],
  supportMatrix: SupportRow[],
): ChipGroup[] => {
  const productionSlugs = new Set(
    supportMatrix
      .filter((row) => row.status === "production")
      .map((row) => row.modelSlug),
  );
  const groups = new Map<string, CatalogChip[]>();
  const push = (modality: string, chip: CatalogChip) => {
    const list = groups.get(modality) ?? [];
    list.push(chip);
    groups.set(modality, list);
  };

  // "New" = introduced in the newest Photon release any family carries.
  const introVersion = (family: ModelFamily) =>
    parseFloat(family.introducedIn.replace(/[^0-9.]/g, "")) || 0;
  const newestIntro = Math.max(...families.map(introVersion));
  const badgeFor = (family: ModelFamily) =>
    introVersion(family) === newestIntro
      ? `New ${family.introducedIn.replace(/[^0-9.]/g, "")}`
      : undefined;

  for (const family of families) {
    // Only models with at least one production support row count.
    const models = releaseModels.filter(
      (m) => m.family === family.name && productionSlugs.has(m.id),
    );
    if (models.length === 0) continue;
    const productionReleases = family.releases.filter(
      (release) => release.status === "production",
    );
    const href = `/photon/models/${family.id}`;

    if (family.versions.length === 0) {
      push(family.modalities, {
        key: family.id,
        name: family.name,
        suffix: productionReleases
          .map((release) => release.name)
          .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
          .join(", "),
        href,
        mark: family.id,
        newBadge: badgeFor(family),
      });
      continue;
    }

    for (const release of productionReleases) {
      const variants = models
        .filter((m) => m.release === release.name && m.variant)
        .map((m) => m.variant as string)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      if (variants.length === 0) continue;
      push(family.modalities, {
        key: `${family.id}-${release.name}`,
        name: familyReleaseLabel(family.name, [release.name]),
        suffix: variants.join(", "),
        href,
        mark: family.id,
        newBadge: badgeFor(family),
      });
    }
  }

  return [...groups.entries()].map(([label, chips]) => ({ label, chips }));
};

// "Moondream VLM" + ["2","3"] → "Moondream VLM 2+3";
// "Qwen 3" + ["3.5"] → "Qwen 3.5" (trailing word absorbed by the release);
// "Qwen3-ASR" + ["3"] → "Qwen3-ASR" (release already in the name).
export const familyReleaseLabel = (familyName: string, releases: string[]) => {
  const shown = releases.filter(
    (r) => !familyName.toLowerCase().includes(r.toLowerCase()),
  );
  if (shown.length === 0) return familyName;
  const words = familyName.split(" ");
  const base =
    words.length > 1 && shown.some((r) => r.startsWith(words[words.length - 1]))
      ? words.slice(0, -1).join(" ")
      : familyName;
  return `${base} ${shown.join("+")}`;
};

// ---- Hardware, grouped by model coverage ----
// Support is a sparse matrix (Moondream runs on many chips; other families
// on few, and even within a family some releases skip chips), so a flat
// chip list would imply every model runs everywhere. Chips are instead
// grouped by the exact family releases with production rows on them: a chip
// that runs every production release reads "All models", the rest are
// labeled with what they actually support ("Moondream VLM 2+3",
// "Moondream VLM 2"). Detail is the tested SKU minus the vendor and the
// chip-name words ("H100" + "H100 SXM 80GB" → "SXM 80GB"; "B200" +
// "NVIDIA B200" → no detail).
export const buildHardwareGroups = (
  chips: string[],
  supportMatrix: SupportRow[],
  families: ModelFamily[],
  releaseModels: ReleaseModel[],
): ChipGroup[] => {
  const production = supportMatrix.filter((r) => r.status === "production");
  const groups = new Map<string, { breadth: number; chips: CatalogChip[] }>();
  // A chip's introduction is its earliest production `since`; the newest
  // cohort gets the "New <version>" corner badge, same as the model chips.
  const introduced: { chip: CatalogChip; since: string }[] = [];
  for (const chip of chips) {
    const rows = production.filter((r) => r.chip === chip);
    if (rows.length === 0) continue;

    // Which of each family's production releases have a model running here.
    const coveredParts: string[] = [];
    const missingParts: string[] = [];
    let covered = 0;
    let total = 0;
    for (const family of families) {
      const productionReleases = family.releases.filter(
        (release) => release.status === "production",
      );
      total += productionReleases.length;
      const chipReleases = productionReleases
        .map((release) => release.name)
        .filter((releaseName) =>
          releaseModels.some(
            (m) =>
              m.family === family.name &&
              m.release === releaseName &&
              rows.some((row) => row.modelSlug === m.id),
          ),
        );
      covered += chipReleases.length;
      if (chipReleases.length > 0)
        coveredParts.push(familyReleaseLabel(family.name, chipReleases));
      const missing = productionReleases
        .map((release) => release.name)
        .filter((releaseName) => !chipReleases.includes(releaseName));
      if (missing.length > 0)
        missingParts.push(familyReleaseLabel(family.name, missing));
    }
    // Nearly-full coverage reads better as what's absent than as a long
    // enumeration of what's present.
    const label =
      covered === total
        ? "All models"
        : missingParts.length < coveredParts.length
          ? `All except ${missingParts.join(" · ")}`
          : coveredParts.join(" · ");

    const vendor = rows[0].target.split(" ")[0];
    const chipWords = new Set(chip.split(" "));
    const suffix = (rows[0].testedHardware ?? "")
      .split(" ")
      .filter((word) => word && word !== vendor && !chipWords.has(word))
      .join(" ");
    const group = groups.get(label) ?? { breadth: covered, chips: [] };
    const entry: CatalogChip = {
      key: slugify(chip),
      name: chip,
      suffix,
      href: `/photon/hardware/${slugify(chip)}`,
    };
    const since = rows
      .map((row) => row.since)
      .filter((value): value is string => value !== null)
      .sort((a, b) => parseFloat(a) - parseFloat(b))[0];
    if (since) introduced.push({ chip: entry, since });
    group.chips.push(entry);
    groups.set(label, group);
  }
  const newestSince = Math.max(
    ...introduced.map((entry) => parseFloat(entry.since)),
  );
  for (const entry of introduced) {
    if (parseFloat(entry.since) === newestSince) {
      entry.chip.newBadge = `New ${entry.since}`;
    }
  }
  // Widest coverage first: "All models", then by covered releases, then name.
  return [...groups.entries()]
    .sort(([a, aGroup], [b, bGroup]) => {
      if (a === "All models") return -1;
      if (b === "All models") return 1;
      const byBreadth = bGroup.breadth - aGroup.breadth;
      return byBreadth !== 0 ? byBreadth : a.localeCompare(b);
    })
    .map(([label, group]) => ({ label, chips: group.chips }));
};

export const MODEL_GROUPS = buildModelGroups(
  MODEL_FAMILIES,
  RELEASE_MODELS,
  SUPPORT_MATRIX,
);

export const HARDWARE_GROUPS = buildHardwareGroups(
  HARDWARE_CHIPS,
  SUPPORT_MATRIX,
  MODEL_FAMILIES,
  RELEASE_MODELS,
);

